import React, { useRef } from 'react';
import { Button } from './ui/button';

interface CardPreviewProps {
  demographics: {
    fullName: string;
    dob: string;
    gender: string;
    address: string;
    photo: string | null; // Base64 photo
  };
  qrCodeImage: string | undefined;
  selectedSize: string;
}

const CardPreview: React.FC<CardPreviewProps> = ({ demographics, qrCodeImage, selectedSize }) => {
  const printRef = useRef<HTMLDivElement | null>(null);

  // Define the DPI and size options in cm
  const DPI = 100;
  const sizeOptions: Record<string, number> = {
    '2x2': 2,
    '3x3': 3,
    '4x4': 4,
    '5x5': 5,
    original: 0, // Original size (no resizing)
  };

  // Calculate the pixel dimensions based on the selected size
  const sizeInCm = sizeOptions[selectedSize] || 0;
  const pixelSize = sizeInCm > 0 ? Math.round((sizeInCm * DPI) / 2.54) : undefined;

  // Handle printing
  const handlePrint = () => {
    if (printRef.current) {
      window.print();
    }
  };

  return (
    <div className="m-4 mx-auto text-center">
      {/* Printable Card Container */}
      <div ref={printRef} className="print-container">
        {/* Front Side Preview */}
        <h2 className="text-xl font-semibold mb-4">Front Side</h2>
        <div
          className="card-container w-[85.6mm] h-[54mm] border border-border rounded-lg overflow-hidden mx-auto flex flex-row bg-card"
        >
          <div className="flex-1 p-4 flex justify-center items-center">
            {demographics.photo && (
              <img
                src={demographics.photo}
                alt="Photo"
                className="w-[75px] h-[75px] rounded-full object-cover"
              />
            )}
          </div>
          <div className="flex-[2] p-4 text-left flex flex-col justify-center">
            <h3 className="text-lg font-semibold mb-2">{demographics.fullName}</h3>
            <p className="text-sm">DOB: {demographics.dob}</p>
            <p className="text-sm">Gender: {demographics.gender}</p>
            <p className="text-sm">Address: {demographics.address}</p>
          </div>
        </div>

        {/* Back Side Preview */}
        <h2 className="text-xl font-semibold mb-4 mt-4">Back Side</h2>
        <div
          className="card-container w-[85.6mm] h-[54mm] border border-border rounded-lg overflow-hidden mx-auto flex justify-center items-center bg-muted p-[4mm]"
        >
          {qrCodeImage ? (
            <img
              src={qrCodeImage}
              alt="QR Code"
              style={{
                width: pixelSize ? `${pixelSize}px` : 'auto',
                height: pixelSize ? `${pixelSize}px` : 'auto',
              }}
            />
          ) : (
            <p className="text-sm text-muted-foreground">QR Code not available</p>
          )}
        </div>
      </div>

      {/* Print Button */}
      <Button
        onClick={handlePrint}
        className="mb-2"
      >
        Print Card
      </Button>
    </div>
  );
};

export default CardPreview;